import { IFormHandler } from "client/Forms";
import { AlertManager, useAlerts } from "client/Hooks";
import { InputValidationResult, InputValidationResultKind } from "client/Validation";
import { useCallback } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { AppType } from "client/Api/Apps/common";
import { prepareGenericPropertiesForSaving } from "./EditAppBase";
import { SaveGenericAppModel } from "client/Api/Apps/EditApp/saveApp";

export function useSaveApp<TModel extends SaveGenericAppModel>(
	formHandler: IFormHandler<TModel>,
	requestHandler: (model: TModel) => Promise<number>,
	appKind: AppType,
	prepareModelForSaving?: (formData: TModel) => TModel
): () => Promise<EditAppSavedResult> {

	const alertManager: AlertManager = useAlerts();

	return useCallback(
		async (): Promise<EditAppSavedResult> => {
			const validationResult: InputValidationResult = await formHandler.validation
				.validate();

			if (validationResult.kind === InputValidationResultKind.Invalid) {
				throw new Error("Validation failed");
			}

			let saveValue: TModel = formHandler.value;
			if (prepareModelForSaving) {
				saveValue = prepareModelForSaving(saveValue);
			}
			saveValue = prepareGenericPropertiesForSaving(saveValue);

			try {
				const id: number = await requestHandler(saveValue);

				return {
					id,
					kind: appKind,
					name: saveValue.name
				};

			} catch (e) {
				if (!formHandler.validation
						.tryProcessServerError(e)
				) {
					alertManager.showError(localize("_ChangesNotSaved"));
				}

				throw new Error("Saving failed");
			}
		},
		[
			alertManager,
			appKind,
			formHandler,
			prepareModelForSaving,
			requestHandler
		]
	);
}

export type EditAppSavedResult = {
	id: number;
	kind: AppType;
	name: string;
};